/**
 * Location Health Debug Page
 * Sprint 4L: Background GPS Sanity
 * 
 * Shows:
 * - Last GPS update timestamp
 * - Current lat/lng
 * - Speed
 * - WebSocket connection status
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRiderStore } from '@/store/rider';
import { wsClient } from '@/lib/ws';
import { ArrowLeft, MapPin, Wifi, WifiOff, Clock, Gauge, Navigation, RefreshCw, AlertTriangle } from 'lucide-react';

interface GPSSnapshot {
  lat: number;
  lng: number;
  accuracy: number;
  speed: number | null;
  heading: number | null;
  timestamp: Date;
}

export default function LocationDebugPage() {
  const navigate = useNavigate();
  const riderStatus = useRiderStore((state) => state.status);
  const currentLocation = useRiderStore((state) => state.currentLocation);
  
  const [wsConnected, setWsConnected] = useState(wsClient.isConnected());
  const [gpsHistory, setGpsHistory] = useState<GPSSnapshot[]>([]);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [gpsWasOffline, setGpsWasOffline] = useState(false);
  
  const watchIdRef = useRef<number | null>(null);

  // Check WS connection periodically
  useEffect(() => {
    const checkConnection = () => {
      setWsConnected(wsClient.isConnected());
    };
    
    const interval = setInterval(checkConnection, 2000);
    return () => clearInterval(interval);
  }, []);

  // Start GPS tracking for debug view
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError('Geolocation API not available');
      return;
    }

    setIsTracking(true);
    setGpsError(null);

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const snapshot: GPSSnapshot = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          speed: position.coords.speed,
          heading: position.coords.heading,
          timestamp: new Date(),
        };

        setLastUpdate(new Date());
        setGpsHistory((prev) => [snapshot, ...prev.slice(0, 19)]); // Keep last 20
        setGpsError(null);
        
        // Clear "was offline" banner after first successful update
        if (gpsWasOffline) {
          setTimeout(() => setGpsWasOffline(false), 3000);
        }
      },
      (error) => {
        setGpsError(error.message);
        setIsTracking(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Detect when GPS was offline and came back
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // App came to foreground - check if GPS was tracking
        if (!isTracking && !gpsError) {
          setGpsWasOffline(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isTracking, gpsError]);

  const formatCoord = (val: number | undefined) => {
    return val !== undefined ? val.toFixed(6) : '--';
  };

  const formatSpeed = (speed: number | null) => {
    if (speed === null || speed === undefined) return '--';
    // Convert m/s to km/h
    return (speed * 3.6).toFixed(1) + ' km/h';
  };

  const formatHeading = (heading: number | null) => {
    if (heading === null || heading === undefined) return '--';
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(heading / 45) % 8;
    return `${heading.toFixed(0)}° ${directions[index]}`;
  };

  const formatTime = (date: Date | null) => {
    if (!date) return '--';
    return date.toLocaleTimeString();
  };

  const timeSinceLastUpdate = () => {
    if (!lastUpdate) return '--';
    const seconds = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s ago`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* GPS Resuming Banner */}
      {gpsWasOffline && (
        <div className="bg-yellow-500 text-yellow-900 px-4 py-2 flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-sm font-medium">GPS was offline, resuming tracking...</span>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-semibold">Location Health</h1>
        <span className="ml-auto text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
          DEBUG
        </span>
      </header>

      <div className="p-4 space-y-4">
        {/* Connection Status */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-sm font-medium text-gray-500 mb-3">Connection Status</h2>
          
          <div className="grid grid-cols-2 gap-4">
            {/* WebSocket */}
            <div className={`p-3 rounded-lg ${wsConnected ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                {wsConnected ? (
                  <Wifi className="w-5 h-5 text-green-600" />
                ) : (
                  <WifiOff className="w-5 h-5 text-red-600" />
                )}
                <span className="text-sm font-medium">WebSocket</span>
              </div>
              <span className={`text-xs ${wsConnected ? 'text-green-700' : 'text-red-700'}`}>
                {wsConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* GPS Status */}
            <div className={`p-3 rounded-lg ${isTracking ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className={`w-5 h-5 ${isTracking ? 'text-green-600' : 'text-red-600'}`} />
                <span className="text-sm font-medium">GPS</span>
              </div>
              <span className={`text-xs ${isTracking ? 'text-green-700' : 'text-red-700'}`}>
                {isTracking ? 'Tracking' : gpsError || 'Not tracking'}
              </span>
            </div>
          </div>

          {/* Rider Status */}
          <div className="mt-3 pt-3 border-t">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Rider Status</span>
              <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                riderStatus === 'available' ? 'bg-green-100 text-green-700' :
                riderStatus === 'busy' ? 'bg-yellow-100 text-yellow-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {riderStatus.charAt(0).toUpperCase() + riderStatus.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Current Location */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-sm font-medium text-gray-500 mb-3">Current Location</h2>
          
          {gpsError ? (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">{gpsError}</span>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Coordinates */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500">Latitude</span>
                    <p className="font-mono text-sm">{formatCoord(currentLocation?.lat)}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Longitude</span>
                    <p className="font-mono text-sm">{formatCoord(currentLocation?.lng)}</p>
                  </div>
                </div>
              </div>

              {/* Speed & Heading */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Gauge className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500 block">Speed</span>
                  <p className="font-medium text-sm">
                    {gpsHistory[0] ? formatSpeed(gpsHistory[0].speed) : '--'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Navigation className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500 block">Heading</span>
                  <p className="font-medium text-sm">
                    {gpsHistory[0] ? formatHeading(gpsHistory[0].heading) : '--'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <MapPin className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500 block">Accuracy</span>
                  <p className="font-medium text-sm">
                    {gpsHistory[0] ? `±${gpsHistory[0].accuracy.toFixed(0)}m` : '--'}
                  </p>
                </div>
              </div>

              {/* Last Update */}
              <div className="flex items-center justify-between text-sm pt-2 border-t">
                <div className="flex items-center gap-2 text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Last update</span>
                </div>
                <span className="font-medium">{timeSinceLastUpdate()}</span>
              </div>
            </div>
          )}
        </div>

        {/* GPS History */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-sm font-medium text-gray-500 mb-3">
            GPS Update History
            <span className="text-gray-400 font-normal"> (last 20)</span>
          </h2>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {gpsHistory.length === 0 ? (
              <p className="text-gray-400 text-sm text-center py-4">
                Waiting for GPS updates...
              </p>
            ) : (
              gpsHistory.map((snapshot, index) => (
                <div
                  key={snapshot.timestamp.getTime()}
                  className={`text-xs p-2 rounded ${
                    index === 0 ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between mb-1">
                    <span className="font-mono text-gray-600">
                      {snapshot.lat.toFixed(6)}, {snapshot.lng.toFixed(6)}
                    </span>
                    <span className="text-gray-400">
                      {formatTime(snapshot.timestamp)}
                    </span>
                  </div>
                  <div className="flex gap-3 text-gray-500">
                    <span>±{snapshot.accuracy.toFixed(0)}m</span>
                    <span>{formatSpeed(snapshot.speed)}</span>
                    <span>{formatHeading(snapshot.heading)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-500">
          <p><strong>GPS Interval:</strong> 5 seconds</p>
          <p><strong>Min Distance:</strong> 10 meters (Haversine filter)</p>
          <p><strong>High Accuracy:</strong> Enabled</p>
        </div>
      </div>
    </div>
  );
}

